/**
 * searchIndex.js
 * Flat list of every setting field used by SearchPalette.
 * Tabs and section titles must match SectionCard `title` props exactly.
 */

const TAB_LABELS = {
    general: "General",
    zoom: "Zoom",
    "zoom-window": "Zoom Window",
    gallery: "Gallery",
    lightbox: "Lightbox",
    mobile: "Mobile",
    video: "Video",
};

function entry(key, label, description, tab, section) {
    return { key, label, description, tab, section, tabLabel: TAB_LABELS[tab] };
}

export const SEARCH_INDEX = [
    // ── General ───────────────────────────────────────────────────────────────
    entry(
        "variationImageChange",
        "Variation Image Change",
        "Swap the main image when a variation with its own image is selected.",
        "general",
        "Variation Images",
    ),
    entry(
        "preloadImages",
        "Preload Images",
        "Preload all gallery and zoom images in the background on page load.",
        "general",
        "Performance",
    ),
    entry(
        "zoomBeforeLoad",
        "Zoom Before Image Loads",
        "Allow zoom to activate without waiting for the image to fully load.",
        "general",
        "Performance",
    ),
    entry(
        "spinner",
        "Enable Spinner",
        "Show an animated loading indicator while images are being fetched.",
        "general",
        "Loading Spinner",
    ),
    entry(
        "spinnerColor",
        "Spinner Color",
        "The color of the loading spinner animation.",
        "general",
        "Loading Spinner",
    ),

    // ── Zoom ──────────────────────────────────────────────────────────────────
    entry(
        "zoomType",
        "Display Mode",
        "Choose between Lens, Window, or Inner zoom type.",
        "zoom",
        "Zoom Type",
    ),
    entry(
        "zoomLevel",
        "Zoom Level",
        "Magnification factor applied when hovering over the product image.",
        "zoom",
        "Zoom Type",
    ),
    entry(
        "zoomOnHover",
        "Zoom on Hover",
        "Enable or disable zoom activation on mouse hover.",
        "zoom",
        "Activation",
    ),
    entry(
        "zoomCursor",
        "Cursor Style",
        "The mouse cursor shown while hovering over the zoomable image.",
        "zoom",
        "Activation",
    ),
    entry(
        "zoomEasing",
        "Enable Easing",
        "Smooth the lens or window movement with an easing animation.",
        "zoom",
        "Easing",
    ),
    entry(
        "zoomEasingAmount",
        "Easing Amount",
        "Controls how quickly the lens or window catches up to the cursor.",
        "zoom",
        "Easing",
    ),
    entry(
        "lensSize",
        "Lens Size",
        "Diameter of the circular zoom lens in pixels.",
        "zoom",
        "Lens Appearance",
    ),
    entry(
        "lensShape",
        "Lens Shape",
        "Shape of the zoom lens — circle or square.",
        "zoom",
        "Lens Appearance",
    ),
    entry(
        "lensBorderColor",
        "Border Color",
        "Color of the lens border ring.",
        "zoom",
        "Lens Appearance",
    ),
    entry(
        "lensBorderWidth",
        "Border Width",
        "Thickness of the lens border in pixels.",
        "zoom",
        "Lens Appearance",
    ),

    // ── Zoom Window ───────────────────────────────────────────────────────────
    entry(
        "zoomWindowSizeMode",
        "Size Mode",
        "How the zoom window size is determined — custom, auto, or match.",
        "zoom-window",
        "Window Size",
    ),
    entry(
        "zoomWindowWidth",
        "Window Width",
        "Width of the zoom window in pixels (custom mode).",
        "zoom-window",
        "Window Size",
    ),
    entry(
        "zoomWindowHeight",
        "Window Height",
        "Height of the zoom window in pixels (custom mode).",
        "zoom-window",
        "Window Size",
    ),
    entry(
        "zoomWindowPosition",
        "Position",
        "Where the zoom window appears relative to the main image.",
        "zoom-window",
        "Position",
    ),
    entry(
        "zoomWindowOffsetX",
        "Offset X",
        "Horizontal gap between the main image and the zoom window.",
        "zoom-window",
        "Position",
    ),
    entry(
        "zoomWindowOffsetY",
        "Offset Y",
        "Vertical offset of the zoom window from the top of the main image.",
        "zoom-window",
        "Position",
    ),
    entry(
        "zoomWindowBorderRadius",
        "Border Radius",
        "Rounded corner radius of the zoom window.",
        "zoom-window",
        "Window Style",
    ),
    entry(
        "zoomWindowBorderColor",
        "Border Color",
        "Color of the zoom window border.",
        "zoom-window",
        "Window Style",
    ),
    entry(
        "zoomWindowBorderWidth",
        "Border Width",
        "Thickness of the zoom window border.",
        "zoom-window",
        "Window Style",
    ),
    entry(
        "zoomWindowShadow",
        "Drop Shadow",
        "Show a drop shadow behind the zoom window.",
        "zoom-window",
        "Window Style",
    ),
    entry(
        "zoomWindowCursor",
        "Cursor Overlay",
        "Show a transparent cursor indicator on the main image in window zoom mode.",
        "zoom-window",
        "Cursor Overlay",
    ),
    entry(
        "zoomWindowCursorSizeMode",
        "Cursor Size Mode",
        "How the cursor overlay size is determined — auto or custom.",
        "zoom-window",
        "Cursor Overlay",
    ),
    entry(
        "zoomWindowCursorWidth",
        "Cursor Width",
        "Custom width of the cursor overlay box.",
        "zoom-window",
        "Cursor Overlay",
    ),
    entry(
        "zoomWindowCursorHeight",
        "Cursor Height",
        "Custom height of the cursor overlay box.",
        "zoom-window",
        "Cursor Overlay",
    ),
    entry(
        "zoomWindowCursorBorderRadius",
        "Cursor Border Radius",
        "Rounded corners of the cursor overlay box.",
        "zoom-window",
        "Cursor Overlay",
    ),
    entry(
        "zoomWindowCursorBg",
        "Cursor Background",
        "Background fill of the cursor overlay.",
        "zoom-window",
        "Cursor Overlay",
    ),
    entry(
        "zoomWindowCursorBorderColor",
        "Cursor Border Color",
        "Border color of the cursor overlay.",
        "zoom-window",
        "Cursor Overlay",
    ),
    entry(
        "zoomWindowCursorBorderWidth",
        "Cursor Border Width",
        "Border thickness of the cursor overlay.",
        "zoom-window",
        "Cursor Overlay",
    ),

    // ── Gallery ───────────────────────────────────────────────────────────────
    entry(
        "mainImageSize",
        "Main Image Size",
        "WordPress image size served for the main display image.",
        "gallery",
        "Image File Sizes",
    ),
    entry(
        "thumbnailImageSize",
        "Thumbnail Image Size",
        "WordPress image size served for thumbnail strip items.",
        "gallery",
        "Image File Sizes",
    ),
    entry(
        "thumbnailShow",
        "Show Thumbnails",
        "Show or hide the thumbnail strip below the main image.",
        "gallery",
        "Thumbnails",
    ),
    entry(
        "thumbnailPosition",
        "Thumbnail Position",
        "Where the thumbnail strip appears relative to the main image.",
        "gallery",
        "Thumbnails",
    ),
    entry(
        "thumbnailLayout",
        "Thumbnail Layout",
        "Slider or grid arrangement for the thumbnail strip.",
        "gallery",
        "Thumbnails",
    ),
    entry(
        "thumbnailTrigger",
        "Thumbnail Trigger",
        "Whether clicking or hovering a thumbnail swaps the main image.",
        "gallery",
        "Thumbnails",
    ),
    entry(
        "thumbnailBreakpoint",
        "Responsive Breakpoint",
        "Gallery width below which side thumbnails move to the bottom.",
        "gallery",
        "Thumbnails",
    ),
    entry(
        "thumbnailWidth",
        "Thumbnail Width",
        "CSS display width of each thumbnail item.",
        "gallery",
        "Thumbnail Size",
    ),
    entry(
        "thumbnailHeight",
        "Thumbnail Height",
        "CSS display height of each thumbnail item.",
        "gallery",
        "Thumbnail Size",
    ),
    entry(
        "thumbnailGap",
        "Thumbnail Gap",
        "Space between thumbnail items in the strip.",
        "gallery",
        "Thumbnail Size",
    ),
    entry(
        "thumbnailBorderRadius",
        "Thumbnail Border Radius",
        "Rounded corner radius for thumbnail items.",
        "gallery",
        "Thumbnail Size",
    ),
    entry(
        "thumbnailBackground",
        "Thumbnail Background Color",
        "Background color applied to each thumbnail item. Images use mix-blend-mode: darken when set.",
        "gallery",
        "Thumbnail Size",
    ),
    entry(
        "thumbnailAspectRatio",
        "Thumbnail Aspect Ratio",
        "Force a fixed aspect ratio on thumbnail items, overriding the height setting.",
        "gallery",
        "Thumbnail Size",
    ),
    entry(
        "thumbnailFitToImage",
        "Fit to Main Image",
        "Stretch thumbnails to fill the height or width of the main image area.",
        "gallery",
        "Thumbnail Size",
    ),
    entry(
        "thumbnailActiveBorderColor",
        "Highlight Color",
        "Border color shown on the currently selected thumbnail.",
        "gallery",
        "Active Thumbnail",
    ),
    entry(
        "thumbnailActiveBorderWidth",
        "Highlight Width",
        "Border thickness of the active thumbnail highlight.",
        "gallery",
        "Active Thumbnail",
    ),
    entry(
        "thumbnailSliderButtons",
        "Navigation Arrows",
        "Show previous/next arrow buttons on the thumbnail slider.",
        "gallery",
        "Slider Options",
    ),
    entry(
        "thumbnailArrowsOverlay",
        "Overlay Arrows",
        "Position slider arrows over the track edges.",
        "gallery",
        "Slider Options",
    ),
    entry(
        "thumbnailDragNav",
        "Drag Navigation",
        "Click-and-drag or touch-drag to scroll through the thumbnail strip.",
        "gallery",
        "Slider Options",
    ),
    entry(
        "thumbnailScrollNav",
        "Scroll Navigation",
        "Use the mouse wheel or trackpad to scroll through the thumbnail strip.",
        "gallery",
        "Slider Options",
    ),
    entry(
        "thumbnailGridColumns",
        "Grid Columns",
        "Number of columns in the thumbnail grid.",
        "gallery",
        "Grid Options",
    ),
    entry(
        "mainImageArrows",
        "Navigation Arrows",
        "Show previous/next arrow buttons on the main image.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImageArrowsAutoHide",
        "Auto-hide Arrows",
        "Hide main image arrows when the cursor leaves the image.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImageArrowIcon",
        "Arrow Icon Style",
        "Shape of the navigation arrow icon — chevron, arrow, caret, angle, or thin.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImageArrowSize",
        "Arrow Size",
        "Diameter of the previous/next arrow buttons on the main image.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImageArrowBorderRadius",
        "Arrow Border Radius",
        "Corner radius of the arrow buttons. 50% gives a circle, 0% gives a square.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImageArrowIconSize",
        "Arrow Icon Size",
        "Size of the SVG arrow icon inside the button.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImageArrowBg",
        "Arrow Background",
        "Background color of the navigation arrow buttons in their normal state.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImageArrowColor",
        "Arrow Icon Color",
        "Icon color of the navigation arrow buttons in their normal state.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImageArrowHoverBg",
        "Arrow Hover Background",
        "Background color of the navigation arrow buttons on hover.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImageArrowHoverColor",
        "Arrow Hover Icon Color",
        "Icon color of the navigation arrow buttons on hover.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImageDotSize",
        "Dot Size",
        "Diameter of the pagination dots. The active dot stretches proportionally.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImageDotColor",
        "Dot Color",
        "Color of the inactive pagination dots.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImageDotActiveColor",
        "Active Dot Color",
        "Color of the currently active pagination dot.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImagePagination",
        "Pagination",
        "Show a pagination indicator below the main image for navigation.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImagePaginationType",
        "Pagination Style",
        "Style of pagination — Dots, Progress Bar, or Count (numeric counter with arrow buttons).",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImagePaginationPosition",
        "Dot Position",
        "Where dot indicators appear — Bottom, Left, or Right. Left/Right enables the Anchor Navigation style.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImagePaginationAutoHide",
        "Auto-hide Pagination",
        "Hide the pagination indicator when the cursor leaves the image.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImageSwipe",
        "Swipe Navigation",
        "Drag or swipe the main image to navigate. Works on desktop (mouse drag) and mobile (touch swipe). Requires Zoom on Hover and Touch Zoom both disabled.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImageScrollNav",
        "Scroll Navigation",
        "Use the mouse scroll wheel to navigate between gallery images. Both scroll directions move to the next or previous image. Thumbnail gallery only.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "mainImageScrollNavDelay",
        "Scroll Response Speed",
        "Minimum time between scroll-triggered navigations. Higher values slow down how fast scrolling moves through images.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "lightboxButtonAutoHide",
        "Auto-hide Expand Button",
        "Hide the lightbox expand button when the cursor leaves the image.",
        "gallery",
        "Main Image Navigation",
    ),
    entry(
        "stackedItemGap",
        "Item Gap",
        "Vertical space between each stacked gallery item (Anchor Navigation mode).",
        "gallery",
        "Stacked Layout",
    ),
    entry(
        "stackedNavigationType",
        "Navigation Type",
        "Type of navigation for the Stacked Layout. Dot Navigation places clickable dots on the side for navigation. Thumbnails shows a vertical strip of thumbnail images instead.",
        "gallery",
        "Stacked Layout",
    ),
    entry(
        "stackedThumbsStickyTop",
        "Sticky Offset",
        "Distance from the top of the viewport when the thumbnail strip is sticky (left/right positions only).",
        "gallery",
        "Stacked Layout",
    ),
    entry(
        "stackedDotsPosition",
        "Dot Navigation",
        "Overlay dots on images or place them in a separate column outside the gallery.",
        "gallery",
        "Stacked Layout",
    ),
    entry(
        "stackedDotGap",
        "Dot Gap",
        "Space between the pagination dots in the stacked layout.",
        "gallery",
        "Stacked Layout",
    ),
    entry(
        "stackedDotSize",
        "Dot Size",
        "Diameter of each pagination dot in the stacked layout.",
        "gallery",
        "Stacked Layout",
    ),
    entry(
        "stackedDotColor",
        "Dot Color",
        "Color of the inactive pagination dots in the stacked layout.",
        "gallery",
        "Stacked Layout",
    ),
    entry(
        "stackedDotActiveColor",
        "Active Dot Color",
        "Color of the currently active pagination dot in the stacked layout.",
        "gallery",
        "Stacked Layout",
    ),
    entry(
        "anchorZoom",
        "Zoom on Hover",
        "Enable zoom when hovering over images in the anchor stacked gallery.",
        "gallery",
        "Zoom",
    ),
    entry(
        "stackedStickyEnabled",
        "Sticky Summary",
        "Keep the product summary fixed in the viewport while scrolling the stacked gallery.",
        "gallery",
        "Sticky Summary",
    ),
    entry(
        "stackedStickySelector",
        "Summary Selector",
        "CSS selector for the product summary element to apply sticky positioning.",
        "gallery",
        "Sticky Summary",
    ),
    entry(
        "stackedStickyTop",
        "Sticky Top Offset",
        "Distance from the top of the viewport when the summary is in sticky position.",
        "gallery",
        "Sticky Summary",
    ),
    entry(
        "stackedStickyBreakpoint",
        "Disable Below",
        "Viewport width below which the sticky summary is disabled. Set to 0 to always enable.",
        "gallery",
        "Sticky Summary",
    ),
    entry(
        "stackedStickyFixOverflow",
        "Fix Theme Overflow",
        "Override overflow:hidden on ancestor elements that block the sticky summary effect.",
        "gallery",
        "Sticky Summary",
    ),
    entry(
        "thumbnailStripGap",
        "Strip Gap",
        "Space between the thumbnail strip and the main image area.",
        "gallery",
        "Main Image",
    ),
    entry(
        "mainImageHeightMode",
        "Height Mode",
        "How the gallery height is managed — Lock, Adaptive, or Fixed pixel height.",
        "gallery",
        "Main Image",
    ),
    entry(
        "mainImageHeight",
        "Fixed Height",
        "Fixed height in pixels for the main image area (used when Height Mode is Fixed).",
        "gallery",
        "Main Image",
    ),
    entry(
        "mainImageFit",
        "Image Fit",
        "How the image fills the display area — contain or cover.",
        "gallery",
        "Main Image",
    ),
    entry(
        "mainImageBackground",
        "Background Color",
        "Background color shown behind the main image.",
        "gallery",
        "Main Image",
    ),
    entry(
        "mainImageBorderRadius",
        "Image Border Radius",
        "Rounded corner radius of the main image and container.",
        "gallery",
        "Main Image",
    ),
    entry(
        "mainImageTransition",
        "Transition Effect",
        "Animation when switching between gallery images.",
        "gallery",
        "Image Transition",
    ),
    entry(
        "mainImageSlideDirection",
        "Slide Direction",
        "Direction images slide when the Slide transition is active — horizontal or vertical.",
        "gallery",
        "Image Transition",
    ),
    entry(
        "mainImageTransitionDuration",
        "Transition Duration",
        "How long the image transition animation takes.",
        "gallery",
        "Image Transition",
    ),
    entry(
        "stickySplitMainSide",
        "Main Side",
        "Which column holds the sticky image viewer — left or right.",
        "gallery",
        "Sticky Split",
    ),
    entry(
        "stickySplitMainWidthPercent",
        "Main Column Width",
        "Percentage of the total gallery width used by the sticky viewer column.",
        "gallery",
        "Sticky Split",
    ),
    entry(
        "stickySplitGap",
        "Column Gap",
        "Space between the sticky viewer column and the scroll column.",
        "gallery",
        "Sticky Split",
    ),
    entry(
        "stickySplitStickyTop",
        "Sticky Top Offset",
        "Distance from the top of the viewport when the main viewer is stuck.",
        "gallery",
        "Sticky Split",
    ),
    entry(
        "stickySplitClickSync",
        "Click Sync",
        "Swap the sticky viewer to the clicked scroll item. Independent of the lightbox click action.",
        "gallery",
        "Sticky Split",
    ),
    entry(
        "stickySplitThumbZoom",
        "Scroll Column Zoom on Hover",
        "Enable zoom on hover for images in the scroll column. Uses the same zoom type as the main viewer.",
        "gallery",
        "Sticky Split",
    ),
    entry(
        "stickySplitScrollItemGap",
        "Item Gap",
        "Vertical space between images in the scroll column.",
        "gallery",
        "Sticky Split",
    ),
    entry(
        "stickySplitScrollItemAspect",
        "Item Aspect Ratio",
        "Aspect ratio enforced on each scroll item — auto uses the natural image ratio.",
        "gallery",
        "Sticky Split",
    ),
    entry(
        "stickySplitScrollItemFit",
        "Item Image Fit",
        "How the image fills the scroll item — contain or cover.",
        "gallery",
        "Sticky Split",
    ),
    entry(
        "stickySplitScrollItemBorderRadius",
        "Item Border Radius",
        "Rounded corner radius applied to each scroll item.",
        "gallery",
        "Sticky Split",
    ),
    entry(
        "stickySplitShowCaption",
        "Show Captions",
        "Show caption text below each scroll item in the scroll column.",
        "gallery",
        "Sticky Split",
    ),
    entry(
        "stickySplitClickAction",
        "Click Action",
        "What happens when a scroll item is clicked — open lightbox or do nothing.",
        "gallery",
        "Sticky Split",
    ),
    entry(
        "stickySplitShowMainInScroll",
        "Include Main Image",
        "Show the main product image as the first item in the scroll column.",
        "gallery",
        "Sticky Split",
    ),

    // ── Hierarchy Grid ────────────────────────────────────────────────────────
    entry(
        "hierarchyGridColumns",
        "Grid Columns",
        "Number of columns in the hierarchy grid below the hero image.",
        "gallery",
        "Hierarchy Grid",
    ),
    entry(
        "hierarchyGridGap",
        "Grid Gap",
        "Space between items in the hierarchy grid.",
        "gallery",
        "Hierarchy Grid",
    ),
    entry(
        "hierarchyGridItemAspect",
        "Item Aspect Ratio",
        "Aspect ratio enforced on hierarchy grid items — auto uses the natural image ratio.",
        "gallery",
        "Hierarchy Grid",
    ),
    entry(
        "hierarchyGridItemFit",
        "Item Image Fit",
        "How images fill grid items — contain or cover.",
        "gallery",
        "Hierarchy Grid",
    ),
    entry(
        "hierarchyGridItemBorderRadius",
        "Item Border Radius",
        "Rounded corner radius applied to each hierarchy grid item.",
        "gallery",
        "Hierarchy Grid",
    ),
    entry(
        "hierarchyGridZoom",
        "Hierarchy Grid Zoom on Hover",
        "Enable zoom when hovering over images in the hierarchy grid.",
        "gallery",
        "Hierarchy Grid",
    ),

    // ── Tiles ─────────────────────────────────────────────────────────────────
    entry(
        "tilesColumns",
        "Tiles Columns",
        "Number of equal-width columns in the tiles grid.",
        "gallery",
        "Tiles",
    ),
    entry(
        "tilesGap",
        "Tiles Gap",
        "Space between tiles in the grid.",
        "gallery",
        "Tiles",
    ),
    entry(
        "tilesItemAspect",
        "Tiles Aspect Ratio",
        "Aspect ratio enforced on each tile — auto uses the natural image ratio.",
        "gallery",
        "Tiles",
    ),
    entry(
        "tilesItemFit",
        "Tiles Image Fit",
        "How images fill each tile — contain or cover.",
        "gallery",
        "Tiles",
    ),
    entry(
        "tilesItemBorderRadius",
        "Tiles Border Radius",
        "Rounded corner radius applied to each tile.",
        "gallery",
        "Tiles",
    ),
    entry(
        "tilesInitialCount",
        "Tiles Initial Count",
        "Number of tiles visible before the Show More button is clicked.",
        "gallery",
        "Tiles",
    ),
    entry(
        "tilesShowMoreEnabled",
        "Show More Button",
        "Show a button at the bottom to reveal hidden tiles beyond the initial count.",
        "gallery",
        "Tiles",
    ),
    entry(
        "tilesShowMoreText",
        "Show More Text",
        "Label on the button that reveals hidden tiles.",
        "gallery",
        "Tiles",
    ),
    entry(
        "tilesShowLessText",
        "Show Less Text",
        "Label on the button that collapses tiles back to the initial count.",
        "gallery",
        "Tiles",
    ),
    entry(
        "tilesZoom",
        "Tiles Zoom on Hover",
        "Enable zoom when hovering over images in the tiles gallery.",
        "gallery",
        "Tiles",
    ),

    entry(
        "mobileBreakpoint",
        "Mobile Breakpoint",
        "Viewport width below which Anchor and Sticky Split layouts collapse to a standard carousel.",
        "mobile",
        "Mobile Fallback",
    ),
    entry(
        "mobileArrows",
        "Mobile Navigation Arrows",
        "Show previous/next arrow buttons in the mobile carousel fallback. Independent of the desktop arrow setting.",
        "mobile",
        "Mobile Fallback",
    ),
    entry(
        "mobilePagination",
        "Mobile Dot Pagination",
        "Show dot indicators in the mobile carousel fallback.",
        "mobile",
        "Mobile Fallback",
    ),
    entry(
        "mobilePaginationPosition",
        "Mobile Dot Position",
        "Where pagination dots appear in the mobile carousel fallback — bottom, left, or right.",
        "mobile",
        "Mobile Fallback",
    ),
    entry(
        "mobileShowThumbs",
        "Show Thumbnails on Mobile",
        "Show the thumbnail strip in the mobile carousel fallback.",
        "mobile",
        "Mobile Fallback",
    ),
    entry(
        "mobileThumbnailPosition",
        "Mobile Thumbnail Position",
        "Where the thumbnail strip appears on mobile — bottom, top, left, or right. Independent of the desktop thumbnail position.",
        "mobile",
        "Mobile Fallback",
    ),
    entry(
        "mobileThumbnailWidth",
        "Mobile Thumbnail Width",
        "Width of each thumbnail item on mobile. Set to 0 to use the desktop thumbnail width.",
        "mobile",
        "Mobile Fallback",
    ),
    entry(
        "mobileThumbnailHeight",
        "Mobile Thumbnail Height",
        "Height of each thumbnail item on mobile. Set to 0 to use the desktop thumbnail height.",
        "mobile",
        "Mobile Fallback",
    ),

    // ── Lightbox ──────────────────────────────────────────────────────────────
    entry(
        "lightbox",
        "Enable Lightbox",
        "Enable the full-screen lightbox image viewer.",
        "lightbox",
        "Lightbox",
    ),
    entry(
        "lightboxOnClick",
        "Open on Button Click",
        "Open the lightbox when the expand button is clicked.",
        "lightbox",
        "Opening Behaviour",
    ),
    entry(
        "lightboxButtonPosition",
        "Expand Button Position",
        "Corner of the main image where the expand button is placed.",
        "lightbox",
        "Opening Behaviour",
    ),
    entry(
        "lightboxOpenOnImageClick",
        "Open on Image Click",
        "Open the lightbox when the main image itself is clicked.",
        "lightbox",
        "Opening Behaviour",
    ),
    entry(
        "lightboxLoop",
        "Loop",
        "Loop back to the first image after the last in the lightbox.",
        "lightbox",
        "Opening Behaviour",
    ),
    entry(
        "lightboxCloseOnOverlay",
        "Close on Overlay Click",
        "Close the lightbox when the background overlay is clicked.",
        "lightbox",
        "Opening Behaviour",
    ),
    entry(
        "lightboxKeyboard",
        "Keyboard Navigation",
        "Enable arrow keys and Escape to navigate and close the lightbox.",
        "lightbox",
        "Opening Behaviour",
    ),
    entry(
        "lightboxSwipe",
        "Swipe / Drag Navigation",
        "Drag or swipe the lightbox image to navigate between images. Works on desktop (mouse drag) and mobile (touch swipe).",
        "lightbox",
        "Opening Behaviour",
    ),
    entry(
        "lightboxOverlayColor",
        "Overlay Background",
        "Background color and opacity of the dark backdrop behind the lightbox image.",
        "lightbox",
        "Display",
    ),
    entry(
        "lightboxSingleImage",
        "Single Image Mode",
        "Show only the clicked image in the lightbox instead of the full gallery.",
        "lightbox",
        "Display",
    ),
    entry(
        "lightboxShowArrows",
        "Navigation Arrows",
        "Show previous and next arrow buttons for navigating between lightbox images.",
        "lightbox",
        "Display",
    ),
    entry(
        "lightboxShowCounter",
        "Show Counter",
        "Display the current image index out of total in the lightbox.",
        "lightbox",
        "Display",
    ),
    entry(
        "lightboxShowCaption",
        "Show Caption",
        "Display the image caption in the lightbox.",
        "lightbox",
        "Display",
    ),
    entry(
        "lightboxShowThumbnails",
        "Show Thumbnails",
        "Show a thumbnail strip at the bottom of the lightbox.",
        "lightbox",
        "Display",
    ),
    entry(
        "lightboxPaginationType",
        "Pagination Type",
        "Style of pagination — thumbnails or dots.",
        "lightbox",
        "Display",
    ),
    entry(
        "lightboxZoomIn",
        "Zoom In/Out",
        "Show zoom in and zoom out buttons in the lightbox toolbar.",
        "lightbox",
        "Toolbar Buttons",
    ),
    entry(
        "lightboxScrollZoom",
        "Mouse Scroll Zoom",
        "Allow mouse wheel scrolling to zoom in and out on the lightbox image.",
        "lightbox",
        "Toolbar Buttons",
    ),
    entry(
        "lightboxShowAutoplayButton",
        "Autoplay Button",
        "Show the slideshow play/pause toggle button in the lightbox toolbar.",
        "lightbox",
        "Toolbar Buttons",
    ),
    entry(
        "lightboxFullscreen",
        "Fullscreen",
        "Show a fullscreen toggle button in the lightbox toolbar.",
        "lightbox",
        "Toolbar Buttons",
    ),
    entry(
        "lightboxImageTools",
        "Image Tools",
        "Show the center toolbar with rotate, flip, and 1:1 zoom buttons.",
        "lightbox",
        "Toolbar Buttons",
    ),
    entry(
        "lightboxShare",
        "Share Button",
        "Show a share button in the lightbox toolbar with social sharing and download options.",
        "lightbox",
        "Toolbar Buttons",
    ),
    entry(
        "lightboxShareFacebook",
        "Share on Facebook",
        "Show the Facebook sharing option in the lightbox share menu.",
        "lightbox",
        "Toolbar Buttons",
    ),
    entry(
        "lightboxShareTwitter",
        "Tweet",
        "Show the Twitter/X sharing option in the lightbox share menu.",
        "lightbox",
        "Toolbar Buttons",
    ),
    entry(
        "lightboxSharePinterest",
        "Pin it",
        "Show the Pinterest sharing option in the lightbox share menu.",
        "lightbox",
        "Toolbar Buttons",
    ),
    entry(
        "lightboxShareDownload",
        "Download Image",
        "Show the download image option in the lightbox share menu.",
        "lightbox",
        "Toolbar Buttons",
    ),
    entry(
        "lightboxZoomOrigin",
        "Zoom From Image",
        "Animate the lightbox open/close by zooming from/to the source image position.",
        "lightbox",
        "Animation",
    ),
    entry(
        "lightboxAnimationDuration",
        "Animation Duration",
        "Duration of the lightbox open/close animation.",
        "lightbox",
        "Animation",
    ),
    entry(
        "lightboxTransition",
        "Transition Effect",
        "Transition style between lightbox images.",
        "lightbox",
        "Animation",
    ),
    entry(
        "lightboxImageBorderRadius",
        "Image Border Radius",
        "Rounded corner radius for images inside the lightbox.",
        "lightbox",
        "Animation",
    ),
    entry(
        "lightboxThumbBorderRadius",
        "Thumbnail Border Radius",
        "Rounded corner radius for thumbnails inside the lightbox.",
        "lightbox",
        "Animation",
    ),
    entry(
        "lightboxAutoplay",
        "Autoplay",
        "Automatically advance to the next image in the lightbox.",
        "lightbox",
        "Slideshow",
    ),
    entry(
        "lightboxAutoplayDelay",
        "Autoplay Delay",
        "Time between slides during autoplay in milliseconds.",
        "lightbox",
        "Slideshow",
    ),

    // ── Video ─────────────────────────────────────────────────────────────────
    entry(
        "videoPosition",
        "Video Position",
        "Where product videos appear in the gallery thumbnail strip — before or after product images.",
        "video",
        "General",
    ),

    // ── Mobile ────────────────────────────────────────────────────────────────
    entry(
        "touchZoom",
        "Touch Zoom",
        "Enable pinch-to-zoom on touch devices.",
        "mobile",
        "Touch Zoom",
    ),
    entry(
        "touchZoomMin",
        "Min Scale",
        "Minimum zoom scale factor on touch devices.",
        "mobile",
        "Touch Zoom",
    ),
    entry(
        "touchZoomMax",
        "Max Scale",
        "Maximum zoom scale factor on touch devices.",
        "mobile",
        "Touch Zoom",
    ),
    entry(
        "swipeThreshold",
        "Swipe Threshold",
        "Minimum swipe distance in pixels required to advance to the next image.",
        "mobile",
        "Swipe Navigation",
    ),
];

/** Compute a stable DOM id from a section title — mirrors SectionCard's own logic. */
export function sectionId(title) {
    return (
        "wcpg-s-" +
        title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "")
    );
}
